<template>
  <div :style="`height: ${height}px; overflow-y: auto; ${backgroundStyle}`">
    <div
      class="w-100 p-3 d-flex align-items-center justify-content-between"
      :class="{
        'border-bottom': showSummary,
        'bg-light': !showSummary,
        'bg-white': showSummary,
      }"
    >
      <span>
        <strong>
          {{ $t("js.backend.ballot_preview.interactive_preview", {}, { locale: backendLocale }) }}
        </strong>
        <AVIcon
          icon="info-circle"
          class="text-dark ms-1 cursor-pointer"
          data-bs-toggle="modal"
          data-bs-target="#ballotPreviewModal"
        />
      </span>

      <div class="d-flex gap-2">
        <button
          :class="`btn btn-sm ${contestsOpened ? 'btn-warning' : 'btn-primary'}`"
          @click="toggleContestsOpened"
          v-text="contestsOpened
            ? $t('js.backend.ballot_preview.close_ballot', {}, { locale: backendLocale })
            : $t('js.backend.ballot_preview.open_ballot', {}, { locale: backendLocale })"
        ></button>
        <select class="btn btn-sm btn-light dropdown-toggle" v-model="selectedLocale">
          <option
            v-for="locale in availableLocales"
            :key="`${locale}-locale`"
            :value="locale"
          >
            {{ $t("js.language_name", {}, { locale: locale }) }}
          </option>
        </select>
      </div>
    </div>

    <div
      v-if="showSummary && votingRound"
      class="w-100 px-3 py-2 d-flex align-items-center gap-1 bg-light shadow-sm"
    >
      <span v-if="votingRound.demo" class="badge text-bg-warning">
        <AVIcon icon="flask" /> {{ t("activerecord.attributes.voting_round.demo") }}
      </span>
      <span v-for="(badge, index) in getBadges(votingRound)" :key="`generated_badge_${index}`" :class="`badge text-bg-${badge.class}`">
        {{ badge.text }}
      </span>
    </div>

    <div class="p-0 m-0" :class="{ 'bg-light': showSummary }">
      <div class="container p-0 px-md-5 pt-sm-3 pt-md-5 pb-5">
        <div class="card border-0 rounded-0 shadow-lg h-100 mb-3">
          <div class="card-header no-accent d-flex flex-column flex-sm-row align-items-stretch bg-theme rounded-0 border-bottom align-items-sm-center p-0">
            <div v-if="themeImg" class="bg-light p-3 align-self-stretch d-flex align-items-center">
              <img :src="themeImg" class="me-3" style="max-height: 50px; max-width: 100%" />
            </div>
            <div class="d-flex flex-grow-1 align-items-center justify-content-between p-3">
              <h3 class="mb-0">{{ electionTitle[locale as SupportedLocale] }}</h3>
            </div>
          </div>
          <div class="card-body h-100 p-3 p-md-5" :dir="isRtl ? 'rtl' : 'ltr'">
            <template v-if="slide.data">
              <header v-if="slide.data.title" class="h1 text-center">{{ slide.data.title[locale] }}</header>
              <div v-if="slide.data.description" class="lead" v-html="slide.data.description[locale]"></div>
            </template>

            <template v-if="contestsOpened">
              <AVSplitHelper
                :image-option='imageOption'
                :key="contests[contestIndex].reference"
                :contest="formatContest(contests[contestIndex])"
                :contestSelection="activeContestSelection"
                :partial-results="partialResults"
                :includeLazyErrors="includeLazyErrors"
                :weight="1"
                :show-submission-helper="true"
                @update:contestSelection="updateActiveContestSelection"
                @update:complete="updateCompleted"
                @update:activePile="updateActivePile"
              />

              <div class="d-flex mt-3 gap-3 flex-column-reverse flex-md-row">
                <button
                  v-if="contestIndex > 0"
                  class="btn btn-lg btn-ballot-outline col-12 col-md-auto"
                  @click="goBack"
                  v-text="$t('js.election_client.actions.back')"
                ></button>
                <button
                  v-if="!isLastContest"
                  class="btn btn-lg btn-theme ms-auto col-12 col-md-auto"
                  @click="goForward"
                  v-text="$t('js.election_client.actions.continue')"
                ></button>
              </div>
            </template>

            <template v-else>
              <ContestHeader
                v-for="contest in orderedContests"
                :contest="(formatContest(contest) as unknown as ConferenceContest)"
                :show-options="true"
                :locale="locale"
              />
              <hr>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="modal fade"
    id="ballotPreviewModal"
    tabindex="-1"
    :aria-labelledby="t('js.backend.ballot_preview.interactive_preview', {}, { locale: backendLocale })"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title fs-5">
            {{ t("js.backend.ballot_preview.modal.title", {}, { locale: backendLocale }) }}
          </h4>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :aria-label="t('backend.shared.modal.close', {}, { locale: backendLocale })"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            <strong>
              {{ t("js.backend.ballot_preview.modal.subtitle", {}, { locale: backendLocale }) }}
            </strong>
          </p>
          <div class="alert alert-warning my-4">
            <AVIcon icon="exclamation-triangle" class="me-1" />
            {{ t("js.backend.ballot_preview.modal.alert", {}, { locale: backendLocale }) }}
          </div>
          <p>
            <strong>
              {{ t("js.backend.ballot_preview.modal.what_to_check", {}, { locale: backendLocale }) }}
            </strong>
          </p>
          <p>{{ t("js.backend.ballot_preview.modal.examples", {}, { locale: backendLocale }) }}</p>
          <ul>
            <li>{{ t("js.backend.ballot_preview.modal.example_1", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_2", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_3", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_4", {}, { locale: backendLocale }) }}</li>
            <li>{{ t("js.backend.ballot_preview.modal.example_5", {}, { locale: backendLocale }) }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import ContestHeader from "@/components/shared/ContestHeader.vue";
import { rtlLanguages } from "@/entrypoints/shared/i18n_constants";
import { BallotSelection } from "@assemblyvoting/js-client";
import { useI18n } from "vue-i18n";
import {
  formatContest,
  orderContests,
  flattenOptions
} from "@/entrypoints/shared/contest_utilities";
import type {
  PropType,
  ConferenceContest,
  LocalString,
  SelectionPile,
  ContestSelection,
  ConferenceOption,
  SupportedLocale,
  ConferenceVotingRound,
  BallotSlide,
  HandRaiseLiveResults,
} from "@/types";

const { locale, t, d } = useI18n();

const props = defineProps({
  contests: {
    type: Array as PropType<ConferenceContest[]>,
    required: true,
  },
  contestPositions: {
    type: Object,
    required: true,
  },
  slide: {
    type: Object as PropType<BallotSlide>,
    required: true,
  },
  electionTitle: {
    type: Object as PropType<LocalString>,
    required: true,
  },
  themeImg: {
    type: String,
    default: null,
  },
  backgroundStyle: {
    type: String,
    default: null,
  },
  height: {
    type: Number,
    default: null,
  },
  votingRound: {
    type: Object as PropType<ConferenceVotingRound>,
    default: null,
  },
  initialLocale: {
    type: String as PropType<SupportedLocale>,
    default: "en",
  },
  backendLocale: {
    type: String as PropType<SupportedLocale>,
    default: "en",
  },
  availableLocales: {
    type: Array as PropType<SupportedLocale[]>,
    default: () => ["en"],
  },
  imageOption: {
    type: String,
    default: null
  },
  initialBallotSelection: {
    type: Object as PropType<BallotSelection | null>,
    default: null,
  },
  showSummary: {
    type: Boolean,
    default: false,
  },
  initialContestsOpened: {
    type: Boolean,
    default: false,
  },
});

const contestIndex = ref<number>(0);

const isLastContest = ref<boolean>(props.contests.length == 1 ? true : false);

const confirming = ref<boolean>(false);

const contestsOpened = ref<boolean>(props.initialContestsOpened);

const partialResults = ref<HandRaiseLiveResults>(null);

const selectedLocale = ref<SupportedLocale>(props.initialLocale);

const includeLazyErrors = ref<boolean>(false);

const activePile = ref<SelectionPile>(null);

const ballotSelection = ref<BallotSelection>(structuredClone(props.initialBallotSelection) ?? null);

const orderedContests = computed(() => orderContests(props.contests, props.contestPositions));

const activeContestSelection = computed(() => ballotSelection.value.contestSelections[contestIndex.value]);

const isRtl = computed(() => rtlLanguages.includes(locale.value));

const goBack = () => {
  includeLazyErrors.value = false;
  contestIndex.value = contestIndex.value - 1;
  confirming.value = false;
  isLastContest.value = contestIndex.value + 1 === props.contests.length;
};

const goForward = () => {
  includeLazyErrors.value = false;
  contestIndex.value = contestIndex.value + 1;
  isLastContest.value = contestIndex.value + 1 === props.contests.length;
};

const toggleContestsOpened = () => contestsOpened.value = !contestsOpened.value;

const updateCompleted = (completed: boolean) => {
  if (completed) includeLazyErrors.value = true;
};

const updateActivePile = (newPile: SelectionPile) => activePile.value = newPile;

const updateActiveContestSelection = (contestSelection: ContestSelection) => {
  ballotSelection.value.contestSelections.splice(
    contestIndex.value,
    1,
    contestSelection,
  );
};

const getBadges = (vr: ConferenceVotingRound) => {
  const today = new Date();
  const fromDate = (vr.status === "scheduled" && vr.schedule_from) ? new Date(vr.schedule_from) : null;
  const toDate = (vr.status === "scheduled" && vr.schedule_to) ? new Date(vr.schedule_to) : null;
  const scheduledOpened = (!fromDate || fromDate < today) && (!toDate || toDate > today);
  const scheduledFuture = fromDate && fromDate > today;
  const scheduledPast =  toDate && toDate < today;
  const badges = [];

  if (vr.status === "open" || (vr.status === "scheduled" && scheduledOpened)) {
    badges.push({
      text: t("js.backend.ballot_preview.voting_round.open"),
      class: "primary",
    });

    if (toDate) {
      badges.push({
        text: t("js.backend.ballot_preview.voting_round.closes_at", {
          date: d(toDate, "long", locale.value)
        }),
        class: "secondary",
      });
    }
  }

  if (vr.status === "closed" || (vr.status === "scheduled" && scheduledPast) || (vr.status === "scheduled" && scheduledFuture)) {
    badges.push({
      text: t("js.backend.ballot_preview.voting_round.closed"),
      class: "warning",
    });

    if (scheduledPast) {
      badges.push({
        text: t("js.backend.ballot_preview.voting_round.closed_at", {
          date: d(toDate, "long", locale.value)
        }),
        class: "secondary",
      });
    };

    if (scheduledFuture) {
      badges.push({
        text: t("js.backend.ballot_preview.voting_round.opens_at", {
          date: d(fromDate, "long", locale.value)
        }),
        class: "secondary",
      });
    };
  }

  return badges;
}

locale.value = selectedLocale.value;

if (!ballotSelection.value) {
  ballotSelection.value = {
    reference: "dummy",
    contestSelections: orderedContests.value.map((contest: ConferenceContest) => {
      return {
        reference: contest.reference,
        piles: [],
      } as ContestSelection;
    }),
  };
};

if (props.slide.handRaise) {
  const initial: HandRaiseLiveResults = {
    blank: {
      reference: "blank",
      results: { count: 0, percentage: 0 },
    },
  };

  flattenOptions(props.contests[0].options).forEach((option: ConferenceOption) => {
    initial[option.reference] = {
      reference: option.reference,
      results: { count: 0, percentage: 0 },
    };
  });

  partialResults.value = initial;
}

watch(selectedLocale, () => locale.value = selectedLocale.value);
</script>
